<template>
	<div :id="tableWrapperId" 
		:class="[ 'single-table-wrapper', {
			'table-mobile-overlay': !isFocused
		}]" 
		@click="toggleTable" 
		@focus="toggleTable" 
		:style="[{
			overflow: (isScrollable) ? 'auto' : 'hidden'
		}]">
		<h3 v-if="data.table.title" :id="tableWrapperId + '-title'" v-html="sanitizedTitle" ref="tableTitle" :class="[{
            'screen-reader-only': data.table.hide_title
        }]"></h3>
		<div 
			v-if="isNarrow && toggleOptions" :class="['expanded', 'button-group', { fixed: isFixed }]"
			:style="[{
				width: (wrapperWidth && isFixed) ? wrapperWidth : '',
			}]">
			<a href="#" aria-role="button" tabindex="0" 
			v-for="(toggleOption, index) in toggleOptions" 
			:key="'toggle-' + index" 
			:class="['button', 'table-toggle', index, {
				active: index!==hiddenColumnClass
			}]" 
			v-on:click.stop.prevent="toggleCompany(index)" 
			@keyup.enter="toggleCompany(index)" 
			v-text="toggleOption.title"></a>
		</div>
		<span v-if="isScrollable" class="scroll-hint-text" v-text="$t('table_scroll_hint')" ref="tableScrollHint" ></span>
		<span v-if="isScrollable && inTabs" class="scroll-hint-text" v-text="$t('bruk_fanene_over')" ref="tableScrollHint" ></span>
		<table 
			role="grid" 
			:aria-labelledby="tableWrapperId + '-title'"
			ref="childTable" 
			:class="[data.table.table_style, {
				'has-focused-cell': hasFocusedCell,
				'force-word-wrap': (typeof data.table.force_word_wrap !== 'undefined' && data.table.force_word_wrap)
			}]" 
			@keyup.prevent="arrowNavigateCells" 
			@click="updateSelectedCell" 
			@focusin="focusTable" 
			@focusout="focusoutTable">	
			<caption v-if="false" v-html="sanitizedTitle"></caption>
			<table-header 
			ref="childHeader" 
			:data="data.table.header" 
			:columnSettings="data.table.column_settings" 
			:hiddenColumnClass="hiddenColumnClass" 
			:isFixed="isFixed" 
			:isNarrow="isNarrow" 
			:isKeyboardNavigating="isKeyboardNavigating" 
			:horizontalScrollOffset="horizontalScrollOffset" 
			:hasFocusedCell="hasFocusedCell" 
			:tableWidth="tableWidth"
			:wrapperWidth="wrapperWidth"
			:columnWidths="columnWidths"></table-header>
			<tbody 
				ref="tableBody" 
				:style="[{
					marginTop: tableBodyMargin,
					width: (wrapperWidth && isFixed) ? wrapperWidth : '',
					display: (wrapperWidth && isFixed) ? 'block' : '',
					overflow: (isScrollable) ? 'auto' : ''
				}]">
				<table-row v-for="(row, index) in data.table.rows" 
				:key="index" 
				:data="row" 
				:rowIndex="index" 
				:columnSettings="data.table.column_settings" 
				:hiddenColumnClass="hiddenColumnClass" 
				:isNarrow="isNarrow" 
				:isFixed="isFixed" 
				:isKeyboardNavigating="isKeyboardNavigating" 
				:hasFocusedCell="hasFocusedCell" 
				:tableWidth="tableWidth" 
				:columnWidths="columnWidths"></table-row>
			</tbody>
		</table>
		<div v-if="data.table.table_footnotes" v-html="sanitizedFootnotes" class="table-footnotes-wrapper">
		</div>
	</div>
</template>


<script>
import DOMPurify from 'dompurify';
export default {
	props: ['data', 'inTabs'],
	data() {
		return {
			isKeyboardNavigating: false,
			hasFocusedCell: false,
			isNarrow: false,
			hiddenColumnClass: 'as',
			toggleOptions: false,
			modeBreakpoint: 800,
			isFixed: false,
			tableBodyMargin: 0,
			isScrollable: false,
			isInTabs: false,
			inOverlay: false,
			isFocused: false,
			tableWidth: 0,
			wrapperWidth: 0,
			columnWidths: null,
			horizontalScrollOffset: 0,
			baseTableRowColumns: false,
			lastScrollOffset: 0
		}
	},
	computed: {
		tableWrapperId() {
			return 'table-' + ((typeof this.data.table.slug !== 'undefined') ? this.data.table.slug : ((this.data.slug || this.data.component_id)))
		},
		sanitizedTitle() {
			return DOMPurify.sanitize(this.data.table.title);
		},
		sanitizedFootnotes() {
			return DOMPurify.sanitize(this.data.table.table_footnotes);
		}
	},
	components: {
		TableHeader: () => import('./Header'),
		TableRow: () => import('./Row')
	},
	methods: {
		toggleTable() {
			this.isFocused = true;
		},
		handleWindowResize() {
			this.isNarrow = (window.innerWidth<=this.modeBreakpoint)
			this.getColumnWidths()
			this.setIsScrollable();
		},
		getRowWithoutColspans() {
			var found = false;
			var count = 0;

			var bodyRows = document.querySelectorAll('#' + this.tableWrapperId + ' table tbody > tr');

			if (bodyRows===null || typeof bodyRows[0] === 'undefined') {
				setTimeout(() => this.getRowWithoutColspans(), 500);
				return;
			}

			while (found===false) {

				// Try to get a row without colspans
				//console.log('check');

				var row = bodyRows[count];
				var columns = row.childNodes;
				var hasColspan = false;
				for (var i=0; i<columns.length; i++) {
					if (columns[i].colSpan>1) {
						hasColspan = true;
					}
				}
				found = (hasColspan===false)
				if (found) {
					this.baseTableRowColumns = row.childNodes;
				}

				count++;

			}

			if (this.baseTableRowColumns===false) {
				this.baseTableRowColumns = bodyRows[0].childNodes;
			}

		},
		adjustColumnWidthsForCompaniesTable(columnWidths) {
			// Since two of the columns are collapsed, we will replace the collapsed column widths with their matching counterparts...
			for (var i=0; i<Object.keys(columnWidths).length; i++) {
				if (columnWidths[i]===0) {
					columnWidths[i] = (typeof columnWidths[i+3] !== 'undefined') ? columnWidths[i+3] : columnWidths[i-3];
				}
			}
			return columnWidths;
		},
		getColumnWidths() {

			if (this.baseTableRowColumns===false) {
				setTimeout(() => { this.getRowWithoutColspans() }, 500);
				return;			
			}

			var wrapper = document.getElementById(this.tableWrapperId);

			if (typeof this.$el === 'undefined' || wrapper===null) {
				//console.log('trying to set table widths')
				setTimeout(() => { this.getColumnWidths() }, 500);
				return;
			}

			var prevIsFixed = this.isFixed;
			this.isFixed = false;
			this.wrapperWidth = 'auto';
			this.tableWidth = 'auto';

			this.wrapperWidth = wrapper.clientWidth + 'px';
			var columnWidths = {}
			var tableWidth = 0;
			for (var i=0; i < this.baseTableRowColumns.length; i++) {
				var elementInfo = this.baseTableRowColumns[i].getBoundingClientRect();
				if (i===0) {
					if (elementInfo.width<=0) return;
				}
				columnWidths[i] = elementInfo.width;
				tableWidth += columnWidths[i];
			}
			this.tableWidth = tableWidth + 'px';
			if (this.data.table.table_style==='companies') {
				columnWidths = this.adjustColumnWidthsForCompaniesTable(columnWidths);
			}
			this.columnWidths = columnWidths;
			this.isFixed = prevIsFixed;
			this.tableBodyMargin = (this.isFixed && typeof this.$refs.childHeader !== 'undefined' && typeof this.$refs.childHeader.$el !== 'undefined') ? this.$refs.childHeader.$el.clientHeight : 0;
			this.setIsScrollable();

		},
		setIsScrollable() {
			if (typeof this.$el === 'undefined') {
				setTimeout(() => { this.setIsScrollable() }, 500);
				return;
			}
			if (this.wrapperWidth === 0 || this.tableWidth === 0) {
				this.getColumnWidths();
				return;
			}
			this.isScrollable = (
				parseInt(this.wrapperWidth.replace('px', '')) - parseInt(this.tableWidth.replace('px', ''))
			) < 0;
		},
		toggleCompany(key) {
			this.hiddenColumnClass = this.toggleOptions[key].class_to_hide
		},
		handleScroll() {
			this.getPosition();
		},
		handleHorizontalTableScroll(event) {
			this.horizontalScrollOffset = (event.target.scrollLeft * -1) + 'px'
		},
		getPosition() {
			if (this.isFixed && (typeof this.$refs.childHeader === 'undefined' || typeof this.$refs.tableTitle === 'undefined' || (this.isScrollable && typeof this.$refs.tableScrollHint === 'undefined'))) {
				setTimeout(() => { 
					this.getPosition() 
				}, 500);
				return;
			}
	
			var	headerOffsetTop = (typeof this.$refs.tableTitle !== 'undefined') ? this.$refs.tableTitle.clientHeight : 0;
			if (this.isScrollable && typeof this.$refs.tableScrollHint !== 'undefined') {
				headerOffsetTop = headerOffsetTop + this.$refs.tableScrollHint.clientHeight;
			}
			
			var elemRect = this.$el.getBoundingClientRect();
			var elemViewportOffset = elemRect.top;
			

			if (window.pageYOffset > this.lastScrollOffset) {
				// down
				this.isFixed = ((elemViewportOffset <= headerOffsetTop) && (elemViewportOffset + elemRect.height >= 250))
			} else {
				// up
				this.isFixed = ((elemViewportOffset <= headerOffsetTop) && (elemViewportOffset + elemRect.height >= 200))
			}

			/*

			if (elemViewportOffset!==0) {
				console.log('---')
				console.log(elemViewportOffset)
				//console.log(elemViewportOffset <= 73)
				console.log(elemRect.top)
				console.log(elemRect.height)
				console.log(elemViewportOffset + elemRect.height >= 200)
				console.log(this.isFixed)
			}

			*/

			this.lastScrollOffset = window.pageYOffset
		},
		addListeners() {
			window.addEventListener('resize', this.handleWindowResize);
			if (!this.inOverlay) {
				window.addEventListener('scroll', this.getPosition);
			} else {
				document.querySelector('#globalOverlay .overlay').addEventListener('scroll', this.getPosition);
			}
		},
		removeListeners() {
			window.removeEventListener('resize', this.handleWindowResize);
			if (!this.inOverlay) {
				window.removeEventListener('scroll', this.getPosition);
			} else {
				document.querySelector('#globalOverlay .overlay').removeEventListener('scroll', this.getPosition);
			}
		},
		getCellIndex(row, cell) {
			return Array.prototype.indexOf.call(row.childNodes, cell);
		},
		focusTable() {
			this.hasFocusedCell = true;
		},
		focusoutTable() {
			this.hasFocusedCell = false;
		},
		updateSelectedCell() {
			this.isKeyboardNavigating = false;
		},
		arrowNavigateCells(e) {
			this.isKeyboardNavigating = true;
			var focusedCell = document.querySelector('td:focus, th:focus');
			if (focusedCell===null) return;
			switch (e.keyCode) {
				case 37:
					// Left
					if (focusedCell.previousSibling!==null) this.focusOnCell(focusedCell.previousSibling)
					break;
				case 38:
					// Up
					this.moveVertically(focusedCell, 'up')
					break;
				case 39:
					// Right
					if (focusedCell.nextSibling!==null) this.focusOnCell(focusedCell.nextSibling)
					break;
				case 40:
					// Down
					this.moveVertically(focusedCell, 'down')
					break;
			}
		},
		moveVertically(focusedCell, direction) {
			var row = focusedCell.parentNode
			if (typeof row !== 'undefined') {
				var index = this.getCellIndex(row, focusedCell)
				var targetRow = (direction==='up') ? row.previousSibling : row.nextSibling
				if (typeof targetRow !== 'undefined' && targetRow !== null) {
					this.focusOnCell(targetRow.childNodes[index])
				} else if (direction==='up') {
					targetRow = document.querySelector('#' + this.tableWrapperId + ' thead tr:last-child');
					if (typeof targetRow !== 'undefined' && targetRow !== null) {
						this.focusOnCell(targetRow.childNodes[index])
					}
				} else if (direction==='down') {
					targetRow = document.querySelector('#' + this.tableWrapperId + ' tbody tr:first-child');
					if (typeof targetRow !== 'undefined' && targetRow !== null) {
						this.focusOnCell(targetRow.childNodes[index])
					}
				}
			} 
		},
		focusOnCell(cell) {
			cell.focus()
		},
	},
	created() {
		this.getRowWithoutColspans();
		this.handleWindowResize();
		if (typeof this.data.table.companies_toggle !== 'undefined') {
			this.toggleOptions = this.data.table.companies_toggle;
			this.hiddenColumnClass = 'as'
		}
	},
	mounted() {
		this.$refs.tableBody.addEventListener('scroll', this.handleHorizontalTableScroll);
		if (document.body.classList.contains('overlay-open')) {
			this.inOverlay = true;
		}
		this.addListeners();
	},
	beforeDestroy() {
		this.$refs.tableBody.removeEventListener('scroll', this.handleHorizontalTableScroll);
		this.removeListeners();
	},
	watch: {
		isFixed() {
			this.getColumnWidths();
		}
	}
}
</script>

<style lang="scss">
	@import "../../../styles/scss/component/table";
</style>